import React, {Component} from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

import {Divider, TextField, Typography, Tooltip, IconButton} from '@material-ui/core';
import {TableRow, TableCell} from '@material-ui/core';
import Button from '../Button';
import {Card, CardContent, CardActions} from '@material-ui/core';
import { Help } from '@material-ui/icons';
import grey from '@material-ui/core/colors/grey';
import PaginatedList from '../PaginatedList';
import { FormattedMessage } from 'react-intl';

/**
 * View of the paginated list of invitations for a subscription
 * Allows to invite more users by entering a comma separated list of emails
 */

const styles = theme => ({
  form: theme.form,
  empty: {
    visibility:"hidden"
  },
  tools: {
    textAlign: "right"
  },
  iconButton: {
    color: grey[600],
  },
  full: {
    overflow: 'auto',
  },
  updateInfo: {
    backgroundColor: grey[200],
    height: 180,
    padding: 24,
    display:'flex', 
    justifyContent:'center'
  },
  center: {
    marginTop: 8,
    display:'flex', 
    justifyContent: 'center',
  },
  tooltip: {
    color: grey[600],
  }
});

// eslint-disable-next-line
const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const EMAIL_SEPARATOR = ",";
const INITIAL_STATE = {
  emails: {
    text: "",
    values: [],
    errors: []
  }
}

class InvitationList extends Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
  }

  /**
   * Input onchange callback
   * Validates emails and sets errors if needed
   * @param {Event} e 
   */
  handleEmails(e) {
    const text = e.target.value;
    const values = text.split(EMAIL_SEPARATOR);
    
    const errors = values
      .map(email=>email.trim())
      .filter(email=>!!email)
      .reduce((errors,email)=>{
      if(!EMAIL_REGEX.test(email)) {
        errors.push({
          message: `Invalid email: ${email}`,
          email
        });
      }
      return errors;
    },[]);

    this.setState({
      emails: {text,values,errors}
    });
  }

  /** 
   * Resets the invitation form to its initial state (closed, empty)
  */
  reset(){
    this.setState(INITIAL_STATE);
  }

  /** 
   * Cancel button callback
   * Resets the form and closes the form
  */
  handleCancel() {
    this.reset();
  }

  /** 
   * Called when the user hits "Send invites"
   * Posts the form and refreshes the view on success
  */
  handleInviteUsers() {
    const {handleInviteUsers} = this.props;
    const {emails} = this.state;
    handleInviteUsers(emails.values)
      .then(action=>{
        if(!action.errors) {
          this.reset();
          this.refresh();
        }
        return action;
      });
  }

  /** 
   * Refreshes the list of invitations by re-loading current page from the API
  */
  refresh() {
    const {
      pagination,
      handleLoadPage
    } = this.props;

    if( pagination ) {
      // FIXME
      handleLoadPage(pagination.limit, pagination.currentToken);
    }
  }

  getErrorCardContent(subscription, navigateToSubscriptionEdit, classes) {
  
    const needToUpdateSubscription = subscription.usedLicencesCount >= subscription.licencesCount;
  
    if(needToUpdateSubscription) {
      return {
        message: 
          <div style={{display:'flex', flexDirection:'row'}}>
            <Typography variant='body2' color='textSecondary' style={{flexGrow:1, alignSelf:'center'}}>
              <FormattedMessage id='subscription.invitations.update.text.updateSubscription' />
            </Typography>
            <Tooltip title="learn more" placement="bottom-end">
              <IconButton
                href="https://sites.google.com/altirnao.com/aolm-faq/my-account-faq"
                target="_blank">
                <Help fontSize='small' className={classes.tooltip}/>
              </IconButton>
            </Tooltip>
          </div>, 
        buttonLabel: <FormattedMessage id='subscription.invitations.update.button.updateSubscription' />, 
        buttonAction: () => navigateToSubscriptionEdit(subscription.id)
      };
    }
  }

  render() {
    const {
      pagination, 
      subscription, 
      invitationsById, 
      classes, 
      handleLoadPage,
      handleCancelInvitation,
      disableInvitations,
      navigateToSubscriptionEdit,
    } = this.props;
    const {emails} = this.state;

    const hasEmailErrors = emails.errors.length > 0;

    const errorCardContent = this.getErrorCardContent(subscription, navigateToSubscriptionEdit, classes);

    return (
    <div>
      <Card className={classes.full}>
        {errorCardContent?
          <div className={classes.updateInfo}>
            <CardContent >
              {errorCardContent.message} 
              <div className={classes.center}>
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  onClick={errorCardContent.buttonAction}>
                    {errorCardContent.buttonLabel} 
                </Button>
              </div>
            </CardContent>
          </div>
          :
          <div className={classes.form}>
            <Divider />
            <CardContent>
              <TextField 
                onChange={e=>this.handleEmails(e)}
                value={emails.text}
                error={hasEmailErrors}
                fullWidth
                label="People to invite"
                helperText="Comma separated list of emails"
              />
            </CardContent>
            <Divider />
            <CardActions>
            <Button
                size="small"
                color="primary"
                onClick={e=>this.handleCancel()}>
                  <FormattedMessage id='subscription.invitations.invite.button.cancel' />
              </Button>
              <Button
                working={subscription.inviting}
                variant="contained"
                size="small"
                color="primary"
                disabled={disableInvitations || hasEmailErrors || emails.values.length===0}
                onClick={e=>this.handleInviteUsers()}>
                  <FormattedMessage id='subscription.invitations.invite.button.sendInvites' />
              </Button>
            </CardActions>
          </div>
        }
        <Divider />
        <PaginatedList
          changeKey={subscription.id}                                                                                                                                                                                                                                   
          pagination={pagination}
          data={invitationsById}
          handleLoadPage={handleLoadPage}
          handleRemove={handleCancelInvitation}
          confirmTitle={<FormattedMessage id="subscription.invitations.confirmation.cancelInvitation.title" />}
          confirmMessage={<FormattedMessage id="subscription.invitations.confirmation.cancelInvitation" />}
          noItem={
            <Typography variant="caption" align="center">
              <FormattedMessage id='subscription.invitations.info.noInvitation' />
            </Typography>
          }
          columns={[
            <FormattedMessage id='subscription.invitations.column.email' />,
            ""
          ]}
          Row={(item, deleteButton)=>(
            <TableRow key={item.id}>
              <TableCell>
                  {item.email}
              </TableCell>
              {deleteButton}
            </TableRow>
          )}
          />
        </Card>
      </div>
    )
  }
}

InvitationList.propTypes = {
  pagination: PropTypes.object, 
  subscription: PropTypes.object.isRequired, 
  invitationsById: PropTypes.object.isRequired, 
  classes: PropTypes.object.isRequired, 
  handleLoadPage: PropTypes.func.isRequired,
}

export default withStyles(styles)(
  InvitationList
);


// WEBPACK FOOTER //
// ./src/components/subscriptions/InvitationList.js